import { RolodexEntryQueryBody, useRolodexEntries } from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import type { Dispatch, SetStateAction } from 'react'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryTagsType, RolodexEntryType } from 'types'
import z from 'zod'

/**
 * Schema for defining a Libra workflow:
 * {
 *   id: ...,
 *   data: {
 *     name: "My amazing workflow",
 *     steps: [
 *       {
 *         name: Step 1 - write config,
 *         component: HelloWorkflowStepWorld,
 *         config: {
 *           name: "ONE",
 *         },
 *       },
 *       {
 *         name: Step 2 - read config,
 *         component: HelloWorkflowStepWorld,
 *         config: {
 *           name: "TWO",
 *         },
 *       },
 *       {
 *         name: Step 3 - Profit,
 *         component: HelloWorkflowStepWorld,
 *         config: {
 *           name: "THREE",
 *         },
 *       },
 *     ],
 *     tags: {
 *       organization: ...,
 *       site: ...,
 *     },
 *   },
 * }
 */

export const LIBRA_WORKFLOW_ENTRY_TYPE = 'libra-workflow' as RolodexEntryType

const libraWorkflowStepSchema = z.object({
  id: z.string(),
  name: z.string(),
  component: z.string(),
  config: z.record(z.string(), z.unknown()).default({}),
})

export type StepValidationStatus = 'Error' | 'Warning' | 'Success'

export type StepValidationResult =
  | { message: string; status: StepValidationStatus }
  | undefined

export interface LibraWorkflowStepProps<
  Config extends Record<string, unknown>,
> {
  name: string
  config: Config
}

export interface LibraWorflowStepComponentProps<
  Config extends Record<string, unknown>,
> extends LibraWorkflowStepProps<Config> {
  setValidationResult: Dispatch<SetStateAction<StepValidationResult>>
}

export type UseValidateConfigHook = (
  props: LibraWorkflowStepProps<Record<string, unknown>>
) => boolean

const libraWorkflowSchema = z.object({
  name: z.string(),
  steps: z.array(libraWorkflowStepSchema),
  showTicketProgress: z.boolean().default(true),
})

export const mapEntryToLibraWorkflow = (entry: GeckoEntry): LibraWorkflow => {
  const parsed = libraWorkflowSchema.parse(entry.data)
  return {
    id: entry.id,
    ...parsed,
    tags: entry.tags,
    // Precompute the index map to avoid components having to do O(n) lookups
    stepIndices: new Map(parsed.steps.map((step, idx) => [step.id, idx])),
  }
}

export interface LibraWorkflow {
  id: string
  name: string
  steps: ReadonlyArray<LibraWorkflowStep>
  tags: RolodexEntryTagsType
  showTicketProgress: boolean
  stepIndices: ReadonlyMap<string, number>
}

export interface LibraWorkflowStep {
  id: string
  name: string
  component: string
  config: Record<string, unknown>
}

export function useLibraWorkflowSteps({
  ids,
  tags,
}: {
  ids?: ReadonlyArray<string>
  tags?: RolodexEntryTagsType
}): {
  libraWorkflows: ReadonlyArray<LibraWorkflow>
  isLoading: boolean
  hasEverLoaded: boolean
  forceRefresh: () => void
} {
  const queryBody = useMemo<RolodexEntryQueryBody | undefined>(() => {
    const hasTags = tags && !isEmpty(tags)
    const hasIds = ids && !isEmpty(ids)
    if (!hasTags && !hasIds) {
      return undefined
    }

    return {
      type: LIBRA_WORKFLOW_ENTRY_TYPE,
      ...(hasTags ? { tags } : {}),
      ...(hasIds ? { ids: [...ids] } : {}),
    }
  }, [ids, tags])

  const {
    entries: libraWorkflows,
    isLoading,
    hasEverLoaded,
    forceRefresh,
  } = useRolodexEntries(queryBody)

  return useMemo(
    () => ({
      libraWorkflows: (libraWorkflows ?? []).map(mapEntryToLibraWorkflow),
      isLoading,
      hasEverLoaded,
      forceRefresh,
    }),
    [forceRefresh, hasEverLoaded, isLoading, libraWorkflows]
  )
}
